import React from 'react';
import styled from 'styled-components';
import {
  BORDER,
  BORDER_SECONDARY,
  LIGHT_GRAY,
  ORANGE_PALE,
  ORANGE_PRIMARY,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../config/Constants';
import { SogoKeisanLevel, SogoKeisanTimeBand } from '../../types/sogoKeisanLevel';

// 総合計算プリントのレベル別評価。計算力レポート（印刷）と同じ見方を画面にも出す。
// 上にレベル別ランクカード、下にレベルごとの区分別グラフ（満点タイムの内訳）を並べる。
// 集計はサーバー（CalcPower::ReportBuilder）側で完結している。

type Props = {
  levels: SogoKeisanLevel[];
  detailPathByDirectoryKey?: Record<string, string>;
  // レベルごとの詳細リンクの文言。講師画面は画像一覧へ、成績タブはカテゴリ別の成績へ飛ばすため差し替える
  detailLinkLabel?: string;
};

const DEFAULT_DETAIL_LINK_LABEL = '画像一覧 ›';

// 一般向けのレベル名（基礎レベル / 難関レベル）。対応表はサーバー（PrintUnit）が持ち、
// payload の levelLabel で渡ってくる。古い保存済み payload には無いので内輪の呼称に倒す。
const levelName = (level: SogoKeisanLevel): string => level.levelLabel ?? level.label;

const formatTime = (sec: number | null): string =>
  sec == null ? '—' : `${Math.floor(sec / 60)}分${String(sec % 60).padStart(2, '0')}秒`;

// 区分の行ラベル。しきい値超の満点は「3分超」（最大しきい値を使う）、満点以外は「満点以外」。
const bandLabel = (band: SogoKeisanTimeBand, bands: SogoKeisanTimeBand[]): string => {
  if (band.thresholdSec != null) return `〜${formatTime(band.thresholdSec)}`;
  if (band.key === 'not_full_mark') return '満点以外';

  const lastThresholdSec = bands.reduce((max, b) => Math.max(max, b.thresholdSec ?? 0), 0);
  return `${formatTime(lastThresholdSec)}超`;
};

// ランクカードだけを描く。左カラムの五角形と高さを揃えるため、区分別グラフとは別に置く。
export const SogoKeisanRankCards: React.FC<{ levels: SogoKeisanLevel[] }> = ({ levels }) => {
  return (
    <DivWrap>
      <DivRankCards>
        {levels.map((level) => (
          <DivRankCard key={level.directoryKey} isUnranked={level.letter == null}>
            <PRankLabel>{levelName(level)}</PRankLabel>
            <PRankValue isUnranked={level.letter == null}>
              {level.letter ?? '?'}
              {level.suffix != null && level.suffix !== '' && <sup>{level.suffix}</sup>}
            </PRankValue>
            {level.letter == null && <PRankCondition>満点{level.minFullMarkCount}枚以上で判定</PRankCondition>}
            <DivRankStats>
              <DtRankStatLabel>取り組み枚数</DtRankStatLabel>
              <DdRankStatValue>
                {level.triedCount}
                <small> 枚</small>
              </DdRankStatValue>
              <DtRankStatLabel>満点平均タイム</DtRankStatLabel>
              <DdRankStatValue>{formatTime(level.bestTimeAvgSec)}</DdRankStatValue>
              <DtRankStatLabel>満点中央値タイム</DtRankStatLabel>
              <DdRankStatValue>{formatTime(level.bestTimeMedianSec)}</DdRankStatValue>
            </DivRankStats>
          </DivRankCard>
        ))}
      </DivRankCards>
    </DivWrap>
  );
};

export const SogoKeisanLevelChart: React.FC<Props> = ({
  levels,
  detailPathByDirectoryKey = {},
  detailLinkLabel = DEFAULT_DETAIL_LINK_LABEL,
}) => {
  // 棒のスケールはタイム帯の最大値基準。数百枚になりうる「未記録」「画像提出なし」は
  // 棒を描くとタイム帯の差が潰れるので、数値だけを出す。
  const maxBandCount = Math.max(
    ...levels.flatMap((level) => level.timeBands.map((band) => band.count)),
    1,
  );

  return (
    <DivWrap>
      {levels.map((level) => {
        const detailPath = detailPathByDirectoryKey[level.directoryKey];

        return (
          // id は「カテゴリ別の成績」から戻ってきたときのアンカー（/menus/records#sogo_keisan_kiso など）
          <DivLevelBlock key={level.directoryKey} id={level.directoryKey}>
            <DivLevelHead>
              <span>
                {levelName(level)}
                <SmallLevelSub>
                  （{level.label}・全{level.totalPrintCount}枚）
                </SmallLevelSub>
              </span>
              {detailPath != null && <ADetailLink href={detailPath}>{detailLinkLabel}</ADetailLink>}
            </DivLevelHead>
            <DivBandGrid>
              {level.timeBands.map((band) => (
                <React.Fragment key={band.key}>
                  <SpanBandLabel>{bandLabel(band, level.timeBands)}</SpanBandLabel>
                  <DivBandTrack>
                    {band.count > 0 && (
                      <SpanBandFill style={{ width: `${Math.min((band.count / maxBandCount) * 100, 100)}%` }} />
                    )}
                  </DivBandTrack>
                  <SpanBandCount>{band.count}枚</SpanBandCount>
                </React.Fragment>
              ))}
              <SpanBandLabel>未記録</SpanBandLabel>
              <span />
              <SpanBandCount>{level.imageOnlyCount}枚</SpanBandCount>
              <SpanBandLabel>画像提出なし</SpanBandLabel>
              <span />
              <SpanBandCount>{level.untouchedCount}枚</SpanBandCount>
            </DivBandGrid>
          </DivLevelBlock>
        );
      })}
    </DivWrap>
  );
};

const DivWrap = styled.div`
  padding: 0.25rem 0.5rem;
`;

const DivRankCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.875rem;
`;

// レポートと同じく flex 縦組みにして、統計（dl）をカード下端に寄せる。
// 3枚のカードで統計の開始位置が揃う。
const DivRankCard = styled.div<{ isUnranked: boolean }>`
  border: 1px ${(p) => (p.isUnranked ? 'dashed #ccc' : `solid ${BORDER}`)};
  border-radius: 6px;
  padding: 0.5rem 0.625rem;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const PRankLabel = styled.p`
  font-size: 0.75rem;
  font-weight: bold;
  color: ${TEXT_PRIMARY};
  margin: 0;
`;

const PRankValue = styled.p<{ isUnranked: boolean }>`
  font-size: 2rem;
  font-weight: bold;
  line-height: 1.15;
  margin: 0.125rem 0 0;
  color: ${(p) => (p.isUnranked ? '#aaa' : TEXT_PRIMARY)};

  sup {
    font-size: 0.4em;
    vertical-align: super;
    letter-spacing: -1px;
  }
`;

const PRankCondition = styled.p`
  font-size: 0.6875rem;
  color: ${TEXT_SECONDARY};
  text-align: center;
  margin: 0.25rem 0 0;
  line-height: 1.3;
`;

const DivRankStats = styled.dl`
  margin: auto 0 0;
  padding: 0.375rem 0.4375rem 0.5rem;
  border-top: 1px solid ${BORDER_SECONDARY};
  text-align: left;
`;

// レポート（印刷）のランクカードと同じ組み方: ラベル小・グレー / 値は太字で一回り大きく
const DtRankStatLabel = styled.dt`
  font-size: 0.6875rem;
  color: ${TEXT_SECONDARY};
  margin-top: 0.375rem;

  &:first-child {
    margin-top: 0;
  }
`;

const DdRankStatValue = styled.dd`
  font-size: 1rem;
  font-weight: bold;
  color: ${TEXT_PRIMARY};
  margin: 0.0625rem 0 0;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.6875rem;
    font-weight: normal;
    color: ${TEXT_SECONDARY};
  }
`;

// アンカーで戻ってきたときに、上部の固定ヘッダー（タイトル＋タブ）で隠れないよう余白を取る。
const ANCHOR_SCROLL_MARGIN_TOP = '6rem';

const DivLevelBlock = styled.div`
  margin-bottom: 0.75rem;
  scroll-margin-top: ${ANCHOR_SCROLL_MARGIN_TOP};
`;

const DivLevelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 0.8125rem;
  font-weight: bold;
  color: ${TEXT_PRIMARY};
  background: ${LIGHT_GRAY};
  border-left: 3px solid ${TEXT_PRIMARY};
  padding: 0.125rem 0.5rem;
  margin-bottom: 0.25rem;
`;

const SmallLevelSub = styled.small`
  font-size: 0.6875rem;
  font-weight: normal;
  color: ${TEXT_SECONDARY};
  margin-left: 0.25rem;
`;

const ADetailLink = styled.a`
  font-size: 0.6875rem;
  font-weight: normal;
`;

const DivBandGrid = styled.div`
  display: grid;
  grid-template-columns: 5.5rem 1fr 3.25rem;
  align-items: center;
  gap: 0.1875rem 0.5rem;
  font-size: 0.6875rem;
  padding-left: 0.375rem;
`;

const SpanBandLabel = styled.span`
  color: ${TEXT_SECONDARY};
  white-space: nowrap;
`;

const DivBandTrack = styled.div`
  height: 11px;
  background: #fff;
  border: 1px solid ${BORDER_SECONDARY};
  position: relative;
`;

const SpanBandFill = styled.span`
  position: absolute;
  top: -1px;
  bottom: -1px;
  left: -1px;
  background: ${ORANGE_PALE};
  border: 1px solid ${ORANGE_PRIMARY};
  box-sizing: content-box;
`;

const SpanBandCount = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${TEXT_SECONDARY};
`;
